// Releases a learner activity for the delivery being taught, and reads that
// release back. The server is the truth: learner tablets discover a release
// there, so the teacher is only told "Activity released" once it is saved.
//
// Releasing creates exactly one release record. It never creates learner
// attempts or evidence, and never changes the delivery's status.

import { IS_PREVIEW } from "../config";
import deliveryRepository from "./deliveryRepository";
import syncDelivery from "./deliverySync";
import { newRelease, releaseKey } from "./releaseAdapter";
import releaseRepository from "./releaseRepository";
import { readJson, writeJson } from "./storage";

const liveReleases = {
  // Reads only. A lesson whose delivery has not reached the server yet cannot
  // have been released.
  async read({ record, activityId }) {
    if (!record?.syncKey) return null;
    const delivery = await deliveryRepository.find(record.syncKey);
    if (!delivery) return null;
    return releaseRepository.find(releaseKey(delivery.id, activityId));
  },

  // Makes sure this lesson's delivery exists (the same idempotent sync the
  // progress loop uses), then gets or creates its one release. A retry after a
  // lost response finds the release already there and keeps its time.
  async release({ record, lessonId, teacherId, classroomId, activityId }) {
    const delivery = await syncDelivery({
      record,
      lessonId,
      teacherId,
      classroomId,
    });
    const key = releaseKey(delivery.id, activityId);
    const existing = await releaseRepository.find(key);
    if (existing) return existing;
    try {
      return await releaseRepository.create(
        newRelease({
          deliveryId: delivery.id,
          activityId,
          releasedAt: new Date().toISOString(),
        })
      );
    } catch (error) {
      const created = await releaseRepository.find(key);
      if (created) return created;
      throw error;
    }
  },
};

// Preview has no server: releases are kept on this device only.
const PREVIEW_KEY = "preview-releases";
const previewKey = (record, activityId) => `${record?.syncKey}:${activityId}`;

const previewReleases = {
  read: async ({ record, activityId }) =>
    readJson(PREVIEW_KEY, {})[previewKey(record, activityId)] || null,
  async release({ record, activityId }) {
    const all = readJson(PREVIEW_KEY, {});
    const k = previewKey(record, activityId);
    if (!all[k]) {
      all[k] = { id: k, releasedAt: new Date().toISOString() };
      writeJson(PREVIEW_KEY, all);
    }
    return all[k];
  },
};

const releases = IS_PREVIEW ? previewReleases : liveReleases;

export const readRelease = (args) => releases.read(args);
export const releaseActivity = (args) => releases.release(args);
